import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../lib/api';
import { Alert, Button, Field, PasswordInput, TextInput } from '../components/ui';
import '../styles/auth.css';

import { EnvBadge } from '../components/EnvBadge';
/**
 * Sign up to post in Explore (specification §11).
 *
 * A separate door from /register, and deliberately so. That one creates a
 * LANDLORD account with a portfolio, a subscription and a receipt counter;
 * somebody who wants to list their plumbing business would end up owning an
 * empty property portfolio — a dead end that looks like success, which is the
 * same trap the sign-in page already warns tenants about.
 *
 * Signing up is not an approval. The account is live immediately; the LISTING
 * waits for a human. Saying that here means nobody submits a business and then
 * wonders for two days why they cannot find it.
 */
export function ProviderSignup() {
  const navigate = useNavigate();
  const { signUpToPost } = useAuth();

  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', password: '', acceptedTerms: false,
    // Who is posting (156). Arriving from "Post a property" asks it first.
    advertiserRole: '' as '' | 'owner' | 'agent' | 'caretaker' | 'individual',
  });
  const forProperty = new URLSearchParams(window.location.search).get('for') === 'property';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: 'fullName' | 'email' | 'phone' | 'password') =>
    (e: { target: { value: string } }) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await signUpToPost({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        acceptedTerms: true,
        ...(form.advertiserRole ? { advertiserRole: form.advertiserRole } : {}),
      });
      // Straight to the thing they came to do, not to a dashboard.
      navigate(forProperty ? '/my-listings' : '/post', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.status === 429
            ? 'Too many attempts. Please try again later.'
            : err.message)
          : 'That could not be sent. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-brand-row">
        <Link to="/home" className="auth-brand">NyumbaSuite</Link>
        <EnvBadge />
      </div>
      <form className="auth-card" onSubmit={submit} noValidate>
        <h1 className="auth-title">{forProperty ? 'Create an account to post a property' : 'List your business'}</h1>
        <p className="auth-lede">
          {forProperty
            ? 'Advertise a home to let, a short stay or a property for sale. You pay to advertise, and we approve every advert before it goes live.'
            : 'Free. Offer a service or sell something in Explore.'}
        </p>

        {error && <div style={{ marginBottom: 16 }}><Alert tone="danger">{error}</Alert></div>}

        <Field label="Your name" htmlFor="s-name">
          <TextInput id="s-name" value={form.fullName} onChange={set('fullName')}
            autoComplete="name" autoFocus required />
        </Field>
        <Field label="Email" htmlFor="s-email">
          <TextInput id="s-email" type="email" value={form.email} onChange={set('email')}
            autoComplete="email" inputMode="email" required />
        </Field>
        <Field label="Phone" htmlFor="s-phone">
          <TextInput id="s-phone" value={form.phone} onChange={set('phone')}
            autoComplete="tel" placeholder="+254 7xx xxx xxx" required />
        </Field>
        <Field label="Posting property as" htmlFor="s-role"
          hint={forProperty ? 'Shown on your adverts, so people know who they are dealing with.' : 'Only if you will advertise property. Leave blank for services and items.'}>
          <select id="s-role" className="input" value={form.advertiserRole} required={forProperty}
            onChange={(e) => setForm(f => ({ ...f, advertiserRole: e.target.value as typeof f.advertiserRole }))}>
            <option value="">{forProperty ? 'Choose one' : 'Not posting property'}</option>
            <option value="owner">Property owner</option>
            <option value="agent">Agent</option>
            <option value="caretaker">Caretaker</option>
            <option value="individual">Individual</option>
          </select>
        </Field>
        <Field label="Password" htmlFor="s-pass" hint="At least 12 characters.">
          <PasswordInput id="s-pass" value={form.password}
            onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
            autoComplete="new-password" required minLength={12} />
        </Field>

        <label className="check" style={{ marginBottom: 20 }}>
          <input type="checkbox" checked={form.acceptedTerms} required
            onChange={(e) => setForm(f => ({ ...f, acceptedTerms: e.target.checked }))} />
          {' '}I agree to the <Link to="/terms">terms</Link> and{' '}
          <Link to="/privacy">privacy policy</Link>.
        </label>

        <Button type="submit" block loading={busy} disabled={!form.acceptedTerms || (forProperty && !form.advertiserRole)}>
          Create account
        </Button>

        <p className="auth-note">
          Your account is ready straight away. What you post is checked by somebody at
          NyumbaSuite before it appears publicly.
        </p>

        <div className="auth-foot">
          <Link to="/login">Already have an account? Sign in</Link>
        </div>

        {/* The same warning the sign-in page gives tenants, pointed the other
            way: a landlord wanting to advertise a UNIT is in the wrong place. */}
        <p className="auth-note">
          Renting out property? <Link to="/register">Create a landlord account</Link> instead —
          that is where units, tenants and rent live.
        </p>
      </form>
      <Link className="auth-link" to="/services">← Back to services</Link>
    </div>
  );
}
